import { useCallback, useEffect, useRef, useState } from 'react';
import GlobalSearchService from '../../Services/GlobalSearchService.js';
import { MIN_QUERY_LENGTH, DEFAULT_TOP_N, GlobalSearchResult, GlobalSearchConcept } from './globalSearchContract';

// Re-exported so existing importers keep one import site for the palette's contract; the definitions
// themselves live in globalSearchContract, which imports nothing and is therefore unit-testable (this
// module reaches msalClient through GlobalSearchService, which needs `window` at import time).
export {
  MIN_QUERY_LENGTH,
  DEFAULT_TOP_N,
  buildStatusMessage,
  buildUnavailableNotice,
  isScopeFullyUnavailable,
  formatLabelList,
  conceptsFromResolvedScope,
  describeRecentSearch,
  nextConceptSelection,
  shouldSearchOnConceptPick,
  entityTypesForConcepts,
  recentSearchesForDisplay,
  capHistory,
  RECENT_SEARCH_HISTORY_LIMIT,
  resultsMatchCurrentQuery,
  addRecentSearch,
  parseRecentSearches,
  RECENT_SEARCH_LIMIT,
} from './globalSearchContract';
export type {
  GlobalSearchHit,
  GlobalSearchGroup,
  GlobalSearchResult,
  GlobalSearchConcept,
  StatusMessageState,
  RecentSearch,
} from './globalSearchContract';

export function useCommandPalette() {
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [result, setResult] = useState<GlobalSearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [concepts, setConcepts] = useState<GlobalSearchConcept[]>([]);
  // The chips the user has picked, by concept key. EMPTY MEANS EVERYTHING the user may see - there is
  // deliberately no "Everything" chip, because deselecting all already says that and a dedicated chip
  // would be a second way to express the same state. The palette shows the resolved scope in words
  // instead, so an empty row never reads as "nothing will be searched".
  const [activeConcepts, setActiveConcepts] = useState<string[]>([]);

  // Held in a ref so reset() can restore the default without depending on concepts, which would rebuild
  // the callback on every load and re-fire the effects that depend on it.
  const defaultConcepts = useRef<string[]>([]);

  // Per-session cache of submitted queries. This is what substitutes for as-you-type: backspacing
  // to a previous query, or re-submitting the same one, costs nothing. Deliberately not persisted -
  // results are RBAC-scoped and can go stale.
  const cache = useRef<Map<string, GlobalSearchResult>>(new Map());

  // Chips are needed the moment the palette opens, so load them on mount rather than on first search.
  // The service shares one promise per session, so mounting the palette repeatedly costs one request.
  useEffect(() => {
    let cancelled = false;

    GlobalSearchService.GetConcepts()
      .then((loaded: GlobalSearchConcept[]) => {
        if (cancelled) return;

        const available = loaded || [];
        setConcepts(available);

        // Start scoped rather than searching everything: an unscoped search fans out across every type
        // and can take seconds, where a scoped one is milliseconds. Nothing is hidden by this - the user
        // can deselect, and a scoped search that finds nothing offers to widen.
        defaultConcepts.current = available.filter((concept) => concept.isDefault).map((concept) => concept.key);
        setActiveConcepts(defaultConcepts.current);
      })
      .catch(() => {
        // Chips are an accelerator, not the feature: if they cannot load, the palette still works by
        // typing, including the "Concept:" prefix. Failing quietly beats an error over a working search.
        if (!cancelled) setConcepts([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const reset = useCallback(() => {
    setQuery('');
    setSubmittedQuery('');
    setResult(null);
    setIsSearching(false);
    setErrorMessage('');
    setValidationMessage('');
    setActiveConcepts(defaultConcepts.current);
    cache.current.clear();
  }, []);

  const search = useCallback(async (rawQuery: string, entityTypes?: string[] | null) => {
    const trimmed = (rawQuery || '').trim();

    setErrorMessage('');

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setValidationMessage(`Type at least ${MIN_QUERY_LENGTH} characters, then press Enter.`);
      setResult(null);
      setSubmittedQuery('');
      return;
    }

    setValidationMessage('');
    setSubmittedQuery(trimmed);

    // The cache key has to include the scope: the same text under a different chip is a different
    // question, and keying on text alone would serve the previous chip's answer.
    const cacheKey = `${(entityTypes || []).join(',')}::${trimmed}`;

    const cached = cache.current.get(cacheKey);
    if (cached) {
      setResult(cached);
      return;
    }

    setIsSearching(true);
    try {
      const response = (await GlobalSearchService.Search(trimmed, DEFAULT_TOP_N, entityTypes)) as GlobalSearchResult;
      const safeResponse: GlobalSearchResult = response || { groups: [] };
      safeResponse.groups = safeResponse.groups || [];

      cache.current.set(cacheKey, safeResponse);
      setResult(safeResponse);
    } catch (error) {
      // Distinct from "no results": an empty list means the search ran and found nothing, which is
      // a different thing for the user to act on than the search having failed.
      setResult(null);
      setErrorMessage('The search could not be completed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, []);

  return {
    query,
    setQuery,
    submittedQuery,
    result,
    isSearching,
    errorMessage,
    validationMessage,
    search,
    reset,
    concepts,
    activeConcepts,
    setActiveConcepts,
  };
}

export default useCommandPalette;
